import { useCallback } from "react"
import { Flex, Modal } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import GenerateForm from "./GenerateForm"
import GeneratedSlotsList from "./GeneratedSlotsList"
import { useUndoRedo } from "./useUndoRedo"
import styles from "./ScheduleExperienceModal.module.css"

export const ScheduleExperienceModal = ({ open, onClose, onConfirm, experienceName, durationMinutes }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const { state: generatedSlots, set, undo, redo, reset, canUndo, canRedo } = useUndoRedo([])

  const handleGenerate = useCallback(
    (newSlots) => {
      const existingLabels = new Set(generatedSlots.map((s) => s.label))
      const unique = newSlots.filter((s) => !existingLabels.has(s.label))
      set([...generatedSlots, ...unique])
    },
    [generatedSlots, set]
  )

  const handleRemoveSlot = useCallback(
    (id) => set(generatedSlots.filter((s) => s.id !== id)),
    [generatedSlots, set]
  )

  const handleClearAll = useCallback(() => set([]), [set])

  const handleConfirm = useCallback(() => {
    onConfirm(generatedSlots)
    reset()
    onClose()
  }, [generatedSlots, onConfirm, onClose, reset])

  const handleClose = () => {
    reset()
    onClose()
  }

  const modalTitle = t("experiences.ticketed_schedule.modal_title")

  const content = (
    <Flex gap={24} align="flex-start" className={styles.modalBody}>
      <GenerateForm
        experienceName={experienceName}
        durationMinutes={durationMinutes}
        onGenerate={handleGenerate}
      />
      <GeneratedSlotsList
        slots={generatedSlots}
        onRemoveSlot={handleRemoveSlot}
        onUndo={undo}
        onRedo={redo}
        onClearAll={handleClearAll}
        canUndo={canUndo}
        canRedo={canRedo}
        onConfirm={handleConfirm}
      />
    </Flex>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => handleClose()} title={modalTitle}>
        {content}
      </CustomDrawer>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={modalTitle}
      footer={null}
      width={1400}
      centered
      className={styles.modal}
    >
      {content}
    </Modal>
  )
}

ScheduleExperienceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  experienceName: PropTypes.string,
  durationMinutes: PropTypes.number
}

ScheduleExperienceModal.defaultProps = {
  experienceName: "",
  durationMinutes: 120
}
